/**
 * The one shared "what seq are we looking at" store for one organism screen
 * (requirement 4: "a scrubber keyed on event seq that drives the brain
 * playback and all panels to that seq"; "scrubbing to a seq twice yields
 * identical panel state").
 *
 * This is intentionally the *only* mutable shared state in the app: every
 * panel (brain, log, portfolio) derives what it shows from `currentSeq` plus
 * its own freshly-fetched data, so two calls to `setSeq(42)` always drive
 * every subscriber to render the same thing — a pure function of
 * (fetched data, seq), never of "how we got here".
 */

export type PlaybackListener = (state: PlaybackState) => void;

export interface PlaybackState {
  /** The seq currently under the scrubber. `null` means "follow live". */
  readonly seq: number | null;
  readonly following: boolean;
  readonly speed: 1 | 10 | 100;
}

export class PlaybackStore {
  private state: PlaybackState = { seq: null, following: true, speed: 1 };
  private readonly listeners = new Set<PlaybackListener>();

  get current(): PlaybackState {
    return this.state;
  }

  subscribe(listener: PlaybackListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /** Scrubs to an explicit seq, leaving "follow live" mode. */
  setSeq(seq: number): void {
    this.state = { ...this.state, seq, following: false };
    this.emit();
  }

  /** Returns to following the head of the published log. */
  followLive(): void {
    this.state = { ...this.state, seq: null, following: true };
    this.emit();
  }

  setSpeed(speed: 1 | 10 | 100): void {
    this.state = { ...this.state, speed };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.state);
  }
}
